import { useEffect, useState } from 'react';
import { WebOCRSDK } from '../../../../sdks/WebOCRSDK';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import axios from 'axios';

import { RGIconComponent } from '../../../PersonalDocument/icons/RgIconComponent';
import { CnhIconComponent } from '../../../PersonalDocument/icons/CnhIconComponent';
import { OthersIconComponent } from '../../../PersonalDocument/icons/OthersIconComponent';
import { CnhDigitalIconComponent } from '../../../PersonalDocument/icons/CnhDigitalIconComponent';
import { PassportIconComponent } from '../../../PersonalDocument/icons/PassportIconComponent';
import { CtpsIconComponent } from '../../../PersonalDocument/icons/CtpsIconComponent';
import { RneIconComponent } from '../../../PersonalDocument/icons/RneIconComponent';

import { ErrorPage } from '../../../ErrorPage';
import { LoadingPage } from '../../../LoadingPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { setDatadogError } from '../../../../services/datadog';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import * as S from './styles';

interface PersonalDocumentProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    signerHash: string;
}

const documentTemplates: Record<string, { icon: any; title: string }> = {
    rg: { icon: RGIconComponent, title: 'Cédula de identidade (RG/CIN)' },
    cnh: { icon: CnhIconComponent, title: 'Carteira de habilitação (CNH)' },
    cnh_digital: { icon: CnhDigitalIconComponent, title: 'Carteira de habilitação digital (CNH)' },
    rg_cin_digital: { icon: CnhDigitalIconComponent, title: 'Cédula de Identidade Digital (RG/CIN)' },
    cin_digital: { icon: RGIconComponent, title: 'Carteira de Identidade Nacional (CIN)' },
    passport: { icon: PassportIconComponent, title: 'Passaporte' },
    rne: { icon: RneIconComponent, title: 'Registro Nacional de Estrangeiros (RNE)' },
    crnm: { icon: RneIconComponent, title: 'Carteira de Registro Nacional Migratório (CRNM)' },
    ctps: { icon: CtpsIconComponent, title: 'Carteira de Trabalho e Previdência Social (CTPS)' },
    others: { icon: OthersIconComponent, title: 'Outros Documentos' },
};

export const SocialSecurityBenefitCardPersonalDocument = ({
    signData,
    setSignerStep,
    setSignData,
    signerHash,
}: PersonalDocumentProps) => {
    const { clientThemeSettings } = useThemeContext();
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(true);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [ocrActive, setOcrActive] = useState(false);

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([loading]);

    const defaultClientDocumentTemplates = ['rg', 'cnh'];
    const clientDocumentTemplates = signData.document_templates ?? defaultClientDocumentTemplates;

    useEffect(() => {
        setLoading(false);
    }, []);

    if (loading) {
        return <LoadingPage clientThemeSettings={clientThemeSettings} />;
    }

    const accentColor = clientThemeSettings?.font_color;

    const submitOCRKeys = async (ocrKeys: any[]) => {
        if (hasMadeRequest) return;

        let payload: Record<string, any> = {};

        if (ocrKeys.length === 1) {
            payload = { ocr_full_document_key: ocrKeys[0]['ocr_key'] };
        } else if (ocrKeys.length === 2) {
            const firstOcrKey = ocrKeys[0];
            const secondOcrKey = ocrKeys[1];
            const firstDocumentArray = firstOcrKey['template'].split('_');
            const ocrFrontKey =
                firstDocumentArray[firstDocumentArray.length - 1] === 'front'
                    ? firstOcrKey['ocr_key']
                    : secondOcrKey['ocr_key'];
            const ocrBackKey =
                firstDocumentArray[firstDocumentArray.length - 1] === 'back'
                    ? firstOcrKey['ocr_key']
                    : secondOcrKey['ocr_key'];
            payload = { ocr_front_key: ocrFrontKey, ocr_back_key: ocrBackKey };
        } else {
            setDatadogError('Unsupported ocr key amount', {
                ocr_key_amount: ocrKeys.length,
                signData,
            });
        }

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        payload = {
            ...payload,
            step: 'personal_document',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((err) => {
                const status = [401, 403, 409].includes(err.response?.status)
                    ? err.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <S.PageWrapper>
            <S.ScrollContent>
                <S.Header>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.Header>

                <S.HeroSection>
                    <S.Title $color={accentColor}>
                        Envie seu <strong>documento</strong>
                    </S.Title>
                </S.HeroSection>

                <S.DocumentsSection>
                    <S.Description>Documentos aceitos:</S.Description>
                    <S.DocumentsList>
                        {clientDocumentTemplates.map((type: string, index: number) => {
                            const doc = documentTemplates[type];
                            if (!doc) return null;
                            const IconComponent = doc.icon;
                            return (
                                <S.DocumentCard key={index} onClick={() => setOcrActive(true)}>
                                        <IconComponent
                                            style={{ width: '56px', height: '56px'}}
                                            size={36}
                                            strokeColor={accentColor || '#1a3a6e'}
                                            strokeWidth={1}
                                        />
                                    <S.DocumentTitle>{doc.title}</S.DocumentTitle>
                                </S.DocumentCard>
                            );
                        })}
                    </S.DocumentsList>
                </S.DocumentsSection>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.FooterSection>
                <Button
                    clientThemeSettings={clientThemeSettings}
                    onClick={hasScrolledToEnd ? () => setOcrActive(true) : scrollToBottom}
                >
                    Continuar
                </Button>

                <PoweredByQITech />
            </S.FooterSection>
            {ocrActive && <WebOCRSDK ocrToken={signData.ocr_token} sessionId={signerHash} clientThemeSettings={clientThemeSettings} documentTypes={clientDocumentTemplates} documentNumber={signData.signer_data?.document_number} imageKey={signData.image_key} onSuccess={submitOCRKeys} onError={() => setOcrActive(false)} />}
        </S.PageWrapper>
    );
};
